import { useState, useEffect, useRef } from "react";

/**
 * Deep comparison function for objects and arrays
 */
function deepEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  
  if (obj1 == null || obj2 == null) return false;
  
  if (typeof obj1 !== typeof obj2) return false;
  
  if (typeof obj1 !== 'object') return obj1 === obj2;
  
  // Handle arrays
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
  
  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
}

/**
 * Hook to track unsaved changes in a form.
 * Compares current values against initial values to determine if there are unsaved changes.
 * 
 * @param {Object} currentValues - Current form values
 * @param {Object} initialValues - Initial form values (what was loaded from server)
 * @returns {Object} { hasUnsavedChanges, markAsSaved, resetChanges }
 */
export function useUnsavedChanges(currentValues, initialValues) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialValuesRef = useRef(initialValues);

  // Update initial values when they change (e.g., when data is loaded)
  useEffect(() => {
    initialValuesRef.current = initialValues;
    setHasUnsavedChanges(false);
  }, [initialValues]);

  // Check for changes whenever current values change
  useEffect(() => {
    const hasChanges = !deepEqual(currentValues, initialValuesRef.current);
    setHasUnsavedChanges(hasChanges);
  }, [currentValues]);

  const markAsSaved = () => {
    initialValuesRef.current = structuredClone ? structuredClone(currentValues) : JSON.parse(JSON.stringify(currentValues));
    setHasUnsavedChanges(false);
  };

  const resetChanges = () => {
    setHasUnsavedChanges(false);
  };

  return {
    hasUnsavedChanges,
    markAsSaved,
    resetChanges
  };
}
